import ApiClient from '@/api/ApiClient';
import BaseService from './BaseService';
import BlogPost from '@/api/models/BlogPost';
import Pagination from '@/api/models/Pagination';
import { env } from '@/composables/config';

class BlogPostService extends BaseService {
    #prefixUrl = 'api/blog_posts/';

    constructor() {
        super();
        this.api = new ApiClient(this.#prefixUrl);
    }

    /**
     * Get all BlogPosts
     * @returns {Pagination}
     */
    async getPaginated(page = 1, limit = 10) {
        const response = await this.api.executeRequest(`/paginated`, { page: page, perPage: limit }, this.api.methods.GET);

        if (response.success() && response.getContent() != null) {
            const pagination = new Pagination(response.getContent());
            
            let blogPostsList = [];
            for (let blogPostData of pagination.data) {
                blogPostsList.push(new BlogPost(blogPostData));
            }

            pagination.setData(blogPostsList);
            response.setContent(pagination);
        }

        return response;
    }

    /**
     * Get all BlogPosts
     * @param {integer} limit
     * @returns {Array}
     */
    async get(limit = null) {
        const response = await this.api.executeRequest(`/`, { limit: limit }, this.api.methods.GET);

        let blogPostsList = [];

        if (response.success() && response.getContent() != null) {
            
            for (let blogPostData of response.getContent()) {
                blogPostsList.push(new BlogPost(blogPostData));
            }
        }

        return blogPostsList;
    }

    /**
     * Saves blogPost by creating or updating
     * @param {Array} blogPost
     * @returns {api/models/Response}
     */
    async save(blogPost = {}) {
        const response = await this.api.executeRequest('/', blogPost, this.api.methods.POST);

        if (response.success() && response.getContent() != null) {
            const blogPostObject = new BlogPost(response.getContent());
            response.setContent(blogPostObject);
        }
        
        return response;
    }

    /**
     * Removes BlogPost
     * @param {integer} blogPostId 
     * @returns {Response}
     */
    async remove(blogPostId = null) {
        return await this.api.executeRequest(`/${blogPostId}`, {}, this.api.methods.DELETE);
    }

    /**
     * Gets BlogPost by id
     * @param {integer} blogPostId 
     * @returns {BlogPost|null}
     */
    async find(blogPostId = null) {
        const response = await this.api.executeRequest(`/find/${blogPostId}`, {}, this.api.methods.GET);

        return response.success() && response.getContent() != null 
            ? new BlogPost(response.getContent())
            : null;
    }

    /**
     * Gets all tags on BlogPosts
     * 
     */
    async getTags() {
        const response = await this.api.executeRequest('/with_tag', {}, this.api.methods.GET);

        const data = response.success() && response.getContent() != null 
            ? response.getContent()
            : null;

        let blogPostList = {
            items: [],
            tags: []
        };

        if (data != null && Array.isArray(data)) {
            const uniqueTags = new Set();

            for (let item of data) {
                const post = new BlogPost(item);
                blogPostList.items.push(post);

                const postTags = post.getTags(); 
                
                if (Array.isArray(postTags)) {
                    postTags.forEach(tag => uniqueTags.add(tag));
                }
            }

            blogPostList.tags = Array.from(uniqueTags);
        }

        return blogPostList;
    }

    /**
     * Gets BlogPost of type ABOUT
     * @returns {BlogPost|null}
     */
    async getAboutBlogPost() {
        const response = await this.api.executeRequest('/about/', {}, this.api.methods.GET);

        return response.success() && response.getContent() != null 
            ? new BlogPost(response.getContent())
            : null;
    } 

    /**
     * Gets Ablotu BlogPost link or ID
     * @param {boolean} getLink - default = true
     * @returns {String|Integer}
     */
    getAboutBlogPostInfo(getLink = true) {
        const id = parseInt(env('ABOUT_BLOG_POST_ID', 100));
        return getLink ? `/blog/detail/${id}` : id;
    }
}

export default new BlogPostService();
